<template>
    <page :busy="busy" :tabs="tabs">
            <router-view @busy="handleBusy"></router-view>
    <div :style="{ '--header-bg-color': primaryColor, '--header-font-color': fontColor }" class="w-100 page-wrapper">
        <div class="d-flex align-items-center flex-column text-center mb-4">
            <img
                v-if="metadata.companyLogo"
                :src="metadata.companyLogo"
                class="header-logo mb-2"
                alt=""
            />
            <h1 class="company-name">{{ metadata.companyName }}</h1>
            <span class="company-address">{{ metadata.address }}</span>
        </div>
        <div class="cards-wrapper w-100 d-flex justify-content-center" v-if="!isCodeSent && !isCodeValidated">
            <b-card class="template responsive-width mb-2">
                <template #header>
                    <b>SMS Opt-in</b> <font-awesome-icon :icon="['fas', 'user']" size="lg" class="mr-2" />
                </template>
                <b-card-body class="d-flex flex-column">
                        <form-phone-number-input
                            floating-label="Phone"
                            v-model="mobilePhone"
                            :default-country-code="defaultCountryCode"
                            class="w-100"
                            @validate="onValidatePhone"
                        ></form-phone-number-input>
                    
                        <form-checkbox v-model="allowSMS" class="w-100 mb-3">
                            <b class="sms-allow-text">Text me reservation details and payment reminders</b>
                        </form-checkbox>
                        <p class="w-100">
                            By checking this option, you agree to receive mobile messages at the phone
                            number provided. Message frequency varies. Message and data rates may apply.
                            View our <a target="_blank" href="/privacy-policy/">Privacy Policy</a> and
                            <a target="_blank" href="/terms-of-service/">Terms of Service</a>
                        </p>
                        
                        <b-button variant="primary" :disabled="!allowSendCode" @click="sendCode">
                            Send Code
                        </b-button>                        
                </b-card-body>
            </b-card>
        </div>
        <div class="cards-wrapper w-100 d-flex justify-content-center" v-if="isCodeSent && !isCodeValidated">
            <b-card class="template responsive-width mb-2">
                <template #header>
                    <b>SMS Opt-in</b> <font-awesome-icon :icon="['fas', 'user']" size="lg" class="mr-2" />
                </template>
                <b-card-body class="d-flex flex-column">
                    <form-input
                        floating-label="Verification Code"
                        v-model="code"
                        class="w-100"
                    ></form-input>
                    <b-button variant="primary" :disabled="!isCodeValid" @click="verifyCode">
                        Verify Code
                    </b-button>                        
                </b-card-body>
            </b-card>
        </div>
        <div class="cards-wrapper w-100 d-flex justify-content-center" v-if="isCodeValidated">
            <b-card class="template responsive-width mb-2">
                <template #header>
                    <b>SMS Opt-in</b> <font-awesome-icon :icon="['fas', 'user']" size="lg" class="mr-2" />
                </template>
                <b-card-body class="d-flex flex-column">
                    You've successfully opted in to receive reservation details and payment reminders via text message.           
                </b-card-body>
            </b-card>
        </div>             
    </div>
    </page>
    
</template>

<script>
import api from '../../api';
import { SMS_OPTIN_SEND_CODE_ENDPOINT, SMS_OPTIN_VERIFY_CODE_ENDPOINT } from '../../endpoints';
import FormInput from '../generic/form-input/form-input.vue';
import FormCheckbox from '../generic/form-checkbox/form-checkbox.vue';
import Page from '../generic/page/page.vue';
import isEmpty from 'lodash/isEmpty';
import { defineAsyncComponent } from 'vue';

const FormPhoneNumberInput = defineAsyncComponent(() =>
    import(/* webpackChunkName: "lx-form-phone-number-input" */
    '../generic/form-phone-number-input/form-phone-number-input.vue')
);

export default {
    components: {
        FormInput,
        FormPhoneNumberInput,
        FormCheckbox,
        Page,
    },
    data() {
        return {
            metadata: {
                companyLogo: window.COMPANY_LOGO,
                companyName: window.COMPANY_NAME,
                address: window.ADDRESS,
                defaultCountryCode: window.DEFAULT_COUNTRY_CODE,
            },
            primaryColor: window.PRIMARY_COLOR,
            fontColor: window.FONT_COLOR,
            smsOptinToken: window.SMS_OPTIN_TOKEN,            
            mobilePhone: '',
            allowSMS: false,
            isPhoneValid: false,
            isCodeSent: false,
            isCodeValidated: false,
            code: '',
        };
    },
    computed: {
        defaultCountryCode() {
            return this.metadata.defaultCountryCode;
        },
        allowSendCode() {
            return this.allowSMS && this.isPhoneValid;
        },
        isCodeValid() {
            return /^\d{6}$/.test(this.code);
        }
    },
    methods: {
        onValidatePhone(value) {
            if (value.valid) {
                this.mobilePhone = value.number;
                this.isPhoneValid = true;
            }
            else {
                this.isPhoneValid = false;
            }
        },
        async sendCode() {
            try {
                this.busy = true;
                await api.post(SMS_OPTIN_SEND_CODE_ENDPOINT(this.smsOptinToken), {
                    'mobile_phone': this.mobilePhone
                });
                this.isCodeSent = true;
                this.successToast({ text: 'SMS code successfully sent.' });
            } catch (e) {
                if (e.response && e.response.data && e.response.data.error) {
                    this.errorToast({ text: e.response.data.error });
                }
                else {
                    this.errorToast({ text: 'Could not send SMS code' });
                }
            } finally {
                this.busy = false; 
            }
        },
        async verifyCode() {
            try {
                this.busy = true;
                await api.post(SMS_OPTIN_VERIFY_CODE_ENDPOINT(this.smsOptinToken), {
                    'code': this.code
                });
                this.isCodeValidated = true;
                this.successToast({ text: 'SMS code successfully validated.' });
            } catch (e) {
                if (e.response && e.response.data && e.response.data.error) {
                    this.errorToast({ text: e.response.data.error });
                }
                else {
                    this.errorToast({ text: 'Could not validate SMS code' });
                }
            } finally {
                this.busy = false; 
            }
        },        
    },
};
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';
    
    .page-wrapper {
        --header-bg-color: #0d47a1;
        --header-font-color: #fff;
        max-width: 1140px;
        margin: 0 auto;
        padding: 20px 20px 50px 20px;
    
        * {
            font-family: 'Lato' !important;
        }
    
        .header-logo {
            max-height: 100px;
            max-width: 200px;
            border: none;
            object-fit: contain;
        }
    
        .thumbnail-image {
            max-width: 300px;
            object-fit: contain;
            max-height: 150px;
        }
    }
    
    :deep(.card-header) {
        color: #fff !important;
        background-color: #0d47a1 !important;
    }
    
    :deep(.sms-allow-text) {
        font-size: 1.1em;
    }

    :deep(.responsive-width) {
        width: 100%;
        
        @media (min-width: 992px) {
             width: 50%;
        }         
    }
</style>
